/**
 * Expense Claim — the controlled vocabularies the ESS screens send and render.
 *
 * Every value below is copied from a CHECK constraint on the claim tables (and
 * matched by the @Pattern rules on the create/update DTOs), so a dropdown
 * selection travels to the API unchanged. Labels are i18n keys only; the
 * screens never render a raw enum value.
 */

/** org_expense_claim.category — the three v1 Local Conveyance categories. */
export const CLAIM_CATEGORY = {
  RATE_PER_KM: "Rate_per_km",
  FIXED_MONTHLY: "Fixed_monthly",
  MAINTENANCE: "Conveyance_maintenance",
};

/** org_expense_claim.claim_status. */
export const CLAIM_STATUS = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  L1_APPROVED: "L1_approved",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  SENT_BACK: "Sent_back",
  PROCESSED: "Processed",
};

/** org_expense_claim.vehicle_type — maintenance claims, honour system. */
export const VEHICLE_TYPE = {
  OWN_BIKE: "Own_Bike",
  OWN_CAR: "Own_Car",
};

/** expense_claim_line.reason — drives Customer / Description / Area visibility. */
export const TRIP_REASON = {
  CUSTOMER_SITE_VISIT: "Customer_Site_Visit",
  OFFICIAL_DUTY: "Official_Duty",
  OTHER: "Other",
};

/**
 * NUMERIC(7,2) on org_expense_claim.total_amount — five integer digits.
 * The claim total is checked against this before the request is sent so the
 * user sees a field error instead of a 400 from bean validation.
 */
export const MAX_TOTAL_AMOUNT = 99999.99;

/**
 * Statuses where the submitter still owns the claim (PRD C.7). Draft is
 * included because a draft has not entered the chain at all.
 */
export const SUBMITTER_WRITABLE_STATUSES = [
  CLAIM_STATUS.DRAFT,
  CLAIM_STATUS.SUBMITTED,
  CLAIM_STATUS.SENT_BACK,
];

export function isSubmitterWritable(status) {
  return SUBMITTER_WRITABLE_STATUSES.includes(status);
}

/* ------------------------------------------------------------ label keys */

export const CLAIM_CATEGORY_LABEL_KEY = {
  [CLAIM_CATEGORY.RATE_PER_KM]: "conveyanceClaim.categoryRatePerKm",
  [CLAIM_CATEGORY.FIXED_MONTHLY]: "conveyanceClaim.categoryFixedMonthly",
  [CLAIM_CATEGORY.MAINTENANCE]: "conveyanceClaim.categoryMaintenance",
};

export const CLAIM_STATUS_LABEL_KEY = {
  [CLAIM_STATUS.DRAFT]: "conveyanceClaim.statusDraft",
  [CLAIM_STATUS.SUBMITTED]: "conveyanceClaim.statusSubmitted",
  [CLAIM_STATUS.L1_APPROVED]: "conveyanceClaim.statusL1Approved",
  [CLAIM_STATUS.APPROVED]: "conveyanceClaim.statusApproved",
  [CLAIM_STATUS.REJECTED]: "conveyanceClaim.statusRejected",
  [CLAIM_STATUS.SENT_BACK]: "conveyanceClaim.statusSentBack",
  [CLAIM_STATUS.PROCESSED]: "conveyanceClaim.statusProcessed",
};

export const VEHICLE_TYPE_LABEL_KEY = {
  [VEHICLE_TYPE.OWN_BIKE]: "localConveyance.modeOwnBike",
  [VEHICLE_TYPE.OWN_CAR]: "localConveyance.modeOwnCar",
};


export const TRIP_REASON_LABEL_KEY = {
  [TRIP_REASON.CUSTOMER_SITE_VISIT]: "conveyanceClaim.reasonCustomerSiteVisit",
  [TRIP_REASON.OFFICIAL_DUTY]: "conveyanceClaim.reasonOfficialDuty",
  [TRIP_REASON.OTHER]: "conveyanceClaim.reasonOther",
};

/** Badge palette, keyed to the status tokens already in styles/globals.css. */
export const CLAIM_STATUS_TONE = {
  [CLAIM_STATUS.DRAFT]: "archived",
  [CLAIM_STATUS.SUBMITTED]: "submitted",
  [CLAIM_STATUS.L1_APPROVED]: "scheduled",
  [CLAIM_STATUS.APPROVED]: "published",
  [CLAIM_STATUS.REJECTED]: "rejected",
  [CLAIM_STATUS.SENT_BACK]: "warning",
  [CLAIM_STATUS.PROCESSED]: "published",
};

/**
 * The submitter's own vehicle type on a per-km trip line maps to the policy
 * mode of the same name — kept as one lookup so the two enums cannot drift.
 */
export function vehicleTypeForMode(mode) {
  if (mode === VEHICLE_TYPE.OWN_BIKE) return VEHICLE_TYPE.OWN_BIKE;
  if (mode === VEHICLE_TYPE.OWN_CAR) return VEHICLE_TYPE.OWN_CAR;
  return null;
}

/* -------------------------------------------------------------- currency */

/**
 * Amounts carry the abbreviation of the currency metric selected on the claim
 * (e.g. "INR"), which comes from the metrics master — no symbol or code is
 * written into this module. Falls back to the bare number while the metric
 * list is still loading or the master is empty.
 */
export function formatAmount(amount, abbreviation) {
  const value = Number(amount ?? 0);
  const fixed = (Number.isFinite(value) ? value : 0).toFixed(2);
  return abbreviation ? `${abbreviation} ${fixed}` : fixed;
}

/**
 * Which currency a form starts on.
 *
 * The metrics master returns every seeded currency sorted by name, so the
 * first row is whatever happens to sort first (AUD before INR). The tenant
 * operates in INR, so that is the default — the user can still pick any other
 * row from the selector, and this is only the starting choice, never a
 * restriction on the list.
 *
 * If the master ever exposes an is_base_currency flag, this is the one place
 * that changes.
 */
export const DEFAULT_CURRENCY_ABBREVIATION = "INR";

export function defaultCurrency(currencies = []) {
  const preferred = currencies.find(
    (currency) =>
      String(currency.abbreviation).trim().toUpperCase() === DEFAULT_CURRENCY_ABBREVIATION
  );
  return preferred ?? currencies[0] ?? null;
}

/** The abbreviation to render an amount with, for a claim's stored metric id. */
export function abbreviationFor(currencies, metricId) {
  if (metricId == null) return "";
  const match = (currencies || []).find(
    (currency) => String(currency.metricId) === String(metricId)
  );
  return match?.abbreviation || "";
}

/* ------------------------------------------------------------------ dates */

/**
 * Month names are i18n keys, not a locale format string, so a claim period
 * reads the same on the ESS list, the approver queue, the finance register and
 * the claim detail view in all four languages.
 *
 * The date parse is local to this file: claimRules already imports from here,
 * so importing its parseIsoDate back would make the two modules circular.
 */
const MONTH_KEYS = [
  "dateTime.monthJan", "dateTime.monthFeb", "dateTime.monthMar", "dateTime.monthApr",
  "dateTime.monthMay", "dateTime.monthJun", "dateTime.monthJul", "dateTime.monthAug",
  "dateTime.monthSep", "dateTime.monthOct", "dateTime.monthNov", "dateTime.monthDec",
];

const pad = (value) => String(value).padStart(2, "0");

function toDate(value) {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Jun 2026" */
export function formatMonth(value, t) {
  const date = toDate(value);
  return date ? `${t(MONTH_KEYS[date.getMonth()])} ${date.getFullYear()}` : "";
}

/** "03 Jun 2026" */
export function formatDate(value, t) {
  const date = toDate(value);
  return date ? `${pad(date.getDate())} ${formatMonth(value, t)}` : "";
}

/** "03 Jun 2026, 15:40" — timestamps carry a time, so no date-only parse. */
export function formatStamp(value, t) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${pad(date.getDate())} ${t(MONTH_KEYS[date.getMonth()])} ${date.getFullYear()}, ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/**
 * The Period / Date column. Per-km claims are month-scoped — one header over
 * many dated lines — so they show the month alone; fixed monthly and
 * maintenance claims carry one date and show the day with it.
 */
export function formatClaimPeriod(claim, t, fallback = "—") {
  const source = claim?.claimMonth || claim?.claimDate;
  if (!source) return fallback;
  const withDay = claim.category !== CLAIM_CATEGORY.RATE_PER_KM && claim.claimDate;
  return (withDay ? formatDate(claim.claimDate, t) : formatMonth(source, t)) || fallback;
}

/* ---------------------------------------------------------------- routes */

/**
 * One form route per claim category, each with a `new` and an edit variant, so
 * a row on the list screen can be linked straight to the correct form from its
 * `category` discriminator without a dispatcher screen in between.
 */
export const CLAIMS_LIST_PATH = "/hrms-ess/conveyance-claims";

export const CLAIM_ROUTE_SEGMENTS = {
  [CLAIM_CATEGORY.RATE_PER_KM]: "rate-per-km",
  [CLAIM_CATEGORY.FIXED_MONTHLY]: "fixed-monthly",
  [CLAIM_CATEGORY.MAINTENANCE]: "maintenance",
};

export function newClaimPath(category) {
  const segment = CLAIM_ROUTE_SEGMENTS[category];
  return segment ? `${CLAIMS_LIST_PATH}/${segment}/new` : CLAIMS_LIST_PATH;
}

export function claimFormPath(category, claimId) {
  const segment = CLAIM_ROUTE_SEGMENTS[category];
  if (!segment) return CLAIMS_LIST_PATH;
  return claimId
    ? `${CLAIMS_LIST_PATH}/${segment}/${claimId}`
    : `${CLAIMS_LIST_PATH}/${segment}/new`;
}
